import { TCreateConverter, TFetchConverter } from 'data/types/converter.types';
import {
  EEventParentsFilterParams,
  TEditEventParentDB,
  TEditEventParentUI,
  TEventParentAttendancesDB,
  TEventParentAttendanceUI,
  TEventParentDataDB,
  TEventParentDataUI,
  TEventParentsExportRequest,
  TEventParentsExportSort,
} from 'data/types/eventParents.types';
import { TSortParams, TTableFilters } from 'data/types/generic.types';
import { FilterValue } from 'antd/es/table/interface';
import { dateConverter, dateShortenFormat } from '@idaho-aeyc/ui-kit';
import { EEventPartType } from 'data/types/events.types';
import { phoneNumberConverter } from './general.converters';
import { eventSessionDateConverter } from './events.convertors';
import { tagsGetConverter } from './tags.convertor';

const EVENT_PART_ORDER: EEventPartType[] = [
  EEventPartType.STANDALONE,
  EEventPartType.PART_ONE,
  EEventPartType.PART_TWO,
  EEventPartType.PART_THREE,
];

const eventParentAttendancesConverter = (
  attendances: TEventParentAttendancesDB | null | undefined,
): TEventParentAttendanceUI[] => {
  if (!attendances) {
    return [];
  }
  return EVENT_PART_ORDER.flatMap(partType => {
    const status = attendances[partType];
    return status ? [{ partType, attendanceStatus: status }] : [];
  });
};

export const getEventParentConvertor: TFetchConverter<
  TEventParentDataUI,
  TEventParentDataDB
> = {
  fromDb: data => {
    return {
      registrantId: data.registrantId,
      eventId: data.eventId,
      eventClassId: data.eventPartId,
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      phone: phoneNumberConverter.fromDb(data.phone) || '',
      childrenNames: data.childrenNames,
      location: data.location,
      ageGroup: data.ageGroup,
      eventType: data.eventType,
      registrationDate: data.registrationDate
        ? dateConverter(data.registrationDate, dateShortenFormat)
        : null,
      eventTitle: data.eventTitle ?? '',
      host: data.host ?? null,
      hostId: data.hostId ?? null,
      hostType: data.hostType ?? null,
      sessionDates: eventSessionDateConverter.fromDb(data.sessionDates),
      tags: tagsGetConverter.fromDb(data.tags),
      attendances: eventParentAttendancesConverter(data.attendances),
    };
  },
};

export const eventParentsFilterConverter = (
  filters: TTableFilters<EEventParentsFilterParams>['filters'],
) => {
  const filtersToDB = {
    ...(filters?.firstName && {
      [EEventParentsFilterParams.FIRST_NAME]: filters.firstName
        .toString()
        .trim(),
    }),
    ...(filters?.lastName && {
      [EEventParentsFilterParams.LAST_NAME]: filters.lastName.toString().trim(),
    }),
    ...(filters?.email && {
      [EEventParentsFilterParams.EMAIL]: filters.email.toString().trim(),
    }),
    ...(filters?.phone && {
      [EEventParentsFilterParams.MOBILE]: filters.phone.toString().trim(),
    }),
    ...(filters?.locationIds && {
      [EEventParentsFilterParams.LOCATION]: filters?.locationIds,
    }),
    ...(filters?.eventTypes && {
      [EEventParentsFilterParams.TYPE]: filters?.eventTypes,
    }),
    ...(filters?.title && {
      [EEventParentsFilterParams.TITLE]: filters.title.toString().trim(),
    }),
    ...(filters?.hostIds && {
      [EEventParentsFilterParams.HOST]: filters?.hostIds,
    }),
    ...(filters?.tagIds && {
      [EEventParentsFilterParams.TAGS]: filters?.tagIds,
    }),
    ...(filters?.attendance?.length && {
      hasAttended: filters.attendance[0] === 'true',
    }),
  };
  return filtersToDB;
};

export const eventParentUpdateConverter: TCreateConverter<
  TEditEventParentUI,
  TEditEventParentDB
> = {
  toDb: data => ({
    firstName: data.firstName,
    lastName: data.lastName,
    email: data.email,
    phone: phoneNumberConverter.toDb(data.phone) || '',
    registrantId: data.registrantId,
    eventId: data.eventId,
  }),
};

const COLUMN_KEY_TO_EXPORT_ID: Record<string, string> = {
  [EEventParentsFilterParams.FIRST_NAME]: 'firstName',
  [EEventParentsFilterParams.LAST_NAME]: 'lastName',
  [EEventParentsFilterParams.MOBILE]: 'phone',
  [EEventParentsFilterParams.EMAIL]: 'email',
  [EEventParentsFilterParams.LOCATION]: 'location',
  [EEventParentsFilterParams.TYPE]: 'eventType',
  [EEventParentsFilterParams.TITLE]: 'eventTitle',
  [EEventParentsFilterParams.HOST]: 'host',
  [EEventParentsFilterParams.ATTENDANCE]: 'attended',
  registrationDate: 'registrationDate',
  sessionDates: 'sessionDates',
  tagIds: 'tags',
};

const SORT_FIELD_MAP: Record<string, string> = {
  [EEventParentsFilterParams.FIRST_NAME]: 'FIRST_NAME',
  [EEventParentsFilterParams.LAST_NAME]: 'LAST_NAME',
  registrationDate: 'REGISTRATION_DATE',
};

export const buildEventParentsExportBody = ({
  filters,
  sortColumn,
  visibleColumnKeys,
  resourceIds,
  isActiveOnly,
}: {
  filters?: Record<string, FilterValue | null>;
  sortColumn?: TSortParams;
  visibleColumnKeys?: string[];
  resourceIds?: string[];
  isActiveOnly?: boolean;
}): TEventParentsExportRequest => {
  const columns = visibleColumnKeys
    ?.map(key => COLUMN_KEY_TO_EXPORT_ID[key])
    .filter(Boolean);

  return {
    ...(columns?.length && { columns }),
    filters: {
      ...(resourceIds?.length && { ids: resourceIds }),
      ...(filters?.firstName && {
        firstName: String(filters.firstName).trim(),
      }),
      ...(filters?.lastName && { lastName: String(filters.lastName).trim() }),
      ...(filters?.email && { email: String(filters.email).trim() }),
      ...(filters?.phone && { phone: String(filters.phone).trim() }),
      ...(filters?.locationIds && {
        locations: filters.locationIds as string[],
      }),
      ...(filters?.eventTypes && {
        eventTypes: filters.eventTypes as string[],
      }),
      ...(filters?.title && { title: String(filters.title).trim() }),
      ...(filters?.hostIds && { hostIds: filters.hostIds as string[] }),
      ...(filters?.tagIds && { tags: filters.tagIds as string[] }),
      ...(filters?.attendance?.length && {
        hasAttended: filters.attendance[0] === 'true',
      }),
      ...(isActiveOnly && { isActiveOnly }),
    },
    ...(sortColumn?.sortField &&
      sortColumn?.sortDirection && {
        sort: {
          field: (SORT_FIELD_MAP[sortColumn.sortField] ||
            sortColumn.sortField) as TEventParentsExportSort['field'],
          direction:
            sortColumn.sortDirection as TEventParentsExportSort['direction'],
        },
      }),
  };
};
